import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { useMembership } from '../hooks/useMembership';

const Navigation: React.FC = () => {
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const tenant = useTenant();
  const [menuOpen, setMenuOpen] = useState(false);
  const { membershipsEnabled, membership } = useMembership();
  // Invite to join until the member has a plan (staff never need one)
  const showJoin =
    membershipsEnabled && (!isAuthenticated || (!!membership && !membership.isStaff && membership.subscriptions.length === 0));

  const isActive = (path: string) => location.pathname === path;

  // Close the mobile menu on navigation
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  return (
    <nav className="navigation">
      <div className="nav-container">
        <Link to="/" className="nav-logo">
          {tenant?.logoUrl ? (
            <img src={tenant.logoUrl} alt={tenant.name} className="nav-logo-img" />
          ) : tenant ? (
            <span className="nav-wordmark">{tenant.name}</span>
          ) : null}
        </Link>

        <button
          className="nav-toggle"
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((open) => !open)}
        >
          {menuOpen ? '✕' : '☰'}
        </button>

        <ul className={`nav-links ${menuOpen ? 'open' : ''}`}>
          <li>
            <Link
              to="/"
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
            >
              Home
            </Link>
          </li>
          <li>
            <Link
              to="/library"
              className={`nav-link ${
                location.pathname === '/library' || location.pathname.startsWith('/library/patterns')
                  ? 'active'
                  : ''
              }`}
            >
              Patterns
            </Link>
          </li>
          <li>
            <Link
              to="/library/moves"
              className={`nav-link ${location.pathname.startsWith('/library/moves') ? 'active' : ''}`}
            >
              Syllabus
            </Link>
          </li>
          {isAuthenticated && (
            <li>
              <Link
                to="/live"
                className={`nav-link ${location.pathname.startsWith('/live') ? 'active' : ''}`}
              >
                Live
              </Link>
            </li>
          )}
          {(user?.role === 'owner' || user?.role === 'instructor') && user?.instructorId != null && (
            <li>
              <Link
                to="/coach"
                className={`nav-link ${isActive('/coach') ? 'active' : ''}`}
              >
                Review
              </Link>
            </li>
          )}
          {showJoin && (
            <li>
              <Link
                to="/join"
                className={`nav-link nav-join ${location.pathname.startsWith('/join') ? 'active' : ''}`}
              >
                Join
              </Link>
            </li>
          )}
          {isAuthenticated && (
            <li>
              <Link
                to="/profile"
                className={`nav-link ${isActive('/profile') ? 'active' : ''}`}
              >
                Profile
              </Link>
            </li>
          )}
          {user?.isAdmin && (
            <li>
              <Link
                to="/admin"
                className={`nav-link admin-link ${location.pathname.startsWith('/admin') ? 'active' : ''}`}
              >
                Admin
              </Link>
            </li>
          )}
          <li>
            {isAuthenticated ? (
              <span className="nav-user">
                <span className="nav-greeting">Hi, {user?.name || 'User'}</span>
                <button onClick={logout} className="btn btn-secondary btn-small nav-auth-btn">
                  Logout
                </button>
              </span>
            ) : (
              <Link to="/login" className="btn btn-primary btn-small nav-auth-btn">
                Login
              </Link>
            )}
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navigation;
